import { useEffect, useMemo } from 'react';

import {
  useCampaignSourceJobs,
  useRetryCampaignSourceJob,
  type Campaign,
  type CampaignSourceJob,
  type CampaignSourceJobStatus,
} from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import { Banner, Button, Card, Pill, Skeleton, type PillTone } from '@barel/ui';

const SOURCE_JOB_POLL_MS = 5_000;
const POLLING_STATUSES = new Set<CampaignSourceJobStatus>(['queued', 'running', 'retrying']);
const RETRYABLE_STATUSES = new Set<CampaignSourceJobStatus>(['stalled', 'error']);

const STATUS_TONE: Record<CampaignSourceJobStatus, PillTone> = {
  queued: 'neutral',
  running: 'accent',
  retrying: 'warning',
  complete: 'success',
  stalled: 'warning',
  error: 'error',
  cancelled: 'neutral',
};

export function shouldPollSourceJob(status: CampaignSourceJobStatus | undefined): boolean {
  return status !== undefined && POLLING_STATUSES.has(status);
}

function timestamp(value: string | number): number {
  const parsed = typeof value === 'number' ? value : Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function latestSourceJob(jobs: readonly CampaignSourceJob[]): CampaignSourceJob | undefined {
  return jobs.reduce<CampaignSourceJob | undefined>((latest, job) => {
    if (!latest) return job;
    return timestamp(job.createdAt) >= timestamp(latest.createdAt) ? job : latest;
  }, undefined);
}

export function sourceProgressCounts(
  job: Pick<CampaignSourceJob, 'ingestedTotal' | 'leadsReceived'>,
): { processed: number; ingested: number; rejected: number } {
  // `ingestedTotal` is the cumulative number of result cards the extension
  // processed. `leadsReceived` is the subset with an addressable LinkedIn
  // identity that the API accepted for campaign seeding.
  const processed = Math.max(0, job.ingestedTotal);
  const ingested = Math.max(0, job.leadsReceived);
  return { processed, ingested, rejected: Math.max(0, processed - ingested) };
}

interface SourceProgressCardProps {
  campaign: Pick<Campaign, 'id' | 'status' | 'sourceUrl'>;
}

export function SourceProgressCard({ campaign }: SourceProgressCardProps) {
  const t = useT('campaigns');
  const sourceJobsQ = useCampaignSourceJobs(campaign.id);
  const retryMut = useRetryCampaignSourceJob();
  const sourceJob = useMemo(
    () => latestSourceJob(sourceJobsQ.data?.sourceJobs ?? []),
    [sourceJobsQ.data?.sourceJobs],
  );
  const poll = !sourceJobsQ.isError && shouldPollSourceJob(sourceJob?.status);
  const refetchSourceJobs = sourceJobsQ.refetch;

  useEffect(() => {
    if (!poll) return;
    const timer = window.setInterval(() => {
      void refetchSourceJobs();
    }, SOURCE_JOB_POLL_MS);
    return () => window.clearInterval(timer);
  }, [poll, refetchSourceJobs]);

  if (sourceJobsQ.isLoading) {
    return (
      <Card padding="lg" radius="2xl" data-testid="source-job-loading">
        <div className="flex flex-col gap-3">
          <Skeleton variant="text" width="35%" />
          <Skeleton variant="rect" height={64} />
        </div>
      </Card>
    );
  }

  if (sourceJobsQ.isError) {
    return (
      <Banner
        tone="error"
        title={t('detail.sourceProgress.loadErrorTitle')}
        data-testid="source-job-error"
        action={
          <Button
            variant="secondary"
            size="sm"
            loading={sourceJobsQ.isFetching}
            onClick={() => void sourceJobsQ.refetch()}
            data-testid="source-job-reload"
          >
            {t('detail.sourceProgress.reload')}
          </Button>
        }
      >
        {t('detail.sourceProgress.loadErrorBody')}
      </Banner>
    );
  }

  if (!sourceJob) {
    const isDraft = campaign.status === 'draft';
    return (
      <Banner
        tone="neutral"
        title={t('detail.sourceProgress.noneTitle')}
        data-testid="source-job-none"
      >
        {t(
          isDraft ? 'detail.sourceProgress.noneDraftBody' : 'detail.sourceProgress.noneActiveBody',
        )}
      </Banner>
    );
  }

  const { processed, ingested, rejected } = sourceProgressCounts(sourceJob);
  const completeZero = sourceJob.status === 'complete' && processed === 0;
  const canRetry = campaign.status === 'active' && RETRYABLE_STATUSES.has(sourceJob.status);
  const canOpenSource =
    Boolean(campaign.sourceUrl) &&
    (sourceJob.status === 'queued' ||
      sourceJob.status === 'running' ||
      sourceJob.status === 'retrying');

  const onRetry = async () => {
    try {
      await retryMut.mutateAsync({ campaignId: campaign.id, jobId: sourceJob.id });
    } catch {
      // The inline alert remains visible until the next explicit attempt or
      // successful mutation; no optimistic status is invented client-side.
    }
  };

  return (
    <Card padding="lg" radius="2xl" data-testid="source-progress-card">
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-tertiary text-xs font-bold uppercase">
              {t('detail.sourceProgress.title')}
            </div>
            <p className="text-secondary text-sm mt-2 mb-0" data-testid="source-job-description">
              {completeZero
                ? t('detail.sourceProgress.completeZero')
                : t(`detail.sourceProgress.body.${sourceJob.status}`)}
            </p>
          </div>
          <Pill
            tone={STATUS_TONE[sourceJob.status]}
            variant="soft"
            size="sm"
            data-testid="source-job-status"
          >
            {t(`detail.sourceProgress.status.${sourceJob.status}`)}
          </Pill>
        </div>

        <dl className="grid grid-cols-1 sm:grid-cols-3 gap-2 m-0">
          <div className="rounded-lg border border-default p-3">
            <dt className="text-tertiary text-xs font-semibold">
              {t('detail.sourceProgress.processed')}
            </dt>
            <dd className="text-primary text-lg font-bold mt-1 ml-0" data-testid="source-processed">
              {processed}
            </dd>
          </div>
          <div className="rounded-lg border border-default p-3">
            <dt className="text-tertiary text-xs font-semibold">
              {t('detail.sourceProgress.ingested')}
            </dt>
            <dd className="text-primary text-lg font-bold mt-1 ml-0" data-testid="source-ingested">
              {ingested}
            </dd>
          </div>
          <div className="rounded-lg border border-default p-3">
            <dt className="text-tertiary text-xs font-semibold">
              {t('detail.sourceProgress.rejected')}
            </dt>
            <dd className="text-primary text-lg font-bold mt-1 ml-0" data-testid="source-rejected">
              {rejected}
            </dd>
          </div>
        </dl>

        <div className="flex flex-wrap items-center gap-2">
          {canOpenSource ? (
            <Button asChild variant="secondary" size="sm">
              <a
                href={campaign.sourceUrl ?? undefined}
                target="_blank"
                rel="noreferrer"
                data-testid="source-job-open-search"
              >
                {t('detail.sourceProgress.openSearch')}
              </a>
            </Button>
          ) : null}
          {canRetry ? (
            <Button
              variant="secondary"
              size="sm"
              loading={retryMut.isPending}
              onClick={() => void onRetry()}
              data-testid="source-job-retry"
            >
              {t('detail.sourceProgress.retry')}
            </Button>
          ) : null}
        </div>

        {retryMut.isError ? (
          <Banner
            tone="error"
            title={t('detail.sourceProgress.retryError')}
            data-testid="source-job-retry-error"
          >
            {t('detail.sourceProgress.retryErrorBody')}
          </Banner>
        ) : null}
      </div>
    </Card>
  );
}
